import {
  AfterContentInit,
  Directive,
  ElementRef,
  HostBinding,
  Input,
  Renderer2,
} from '@angular/core';
import { cloneDeep, isEmpty } from 'lodash-es';

import { environment } from '../../../../environments/environment';
import { GTMService, GTMEvent } from '../../services';
import { GTMEventModel } from '../../services/gtm';
import { viguideVersion } from '../../../../../package-version';

declare global {
  interface Window {
    dataLayer: any[];
  }
}

@Directive({
  selector: '[gtm], [gtmAction], [gtmLabel], [gtmCategory]',
  standalone: true,
})
export class GtmDirective implements AfterContentInit {
  @Input() gtm: string;
  @Input() gtmOn: string[] = ['click'];
  @Input() gtmAction: string;
  @Input() gtmLabel: string;
  @Input() gtmCategory: string;
  @Input() gtmId: string | number;
  @Input() gtmProperties: any = {};
  @HostBinding() id: string;

  private gtmEvent: GTMEvent;

  constructor(
    private elRef: ElementRef,
    private renderer: Renderer2,
    private gtmService: GTMService
  ) {}

  ngAfterContentInit(): void {
    if (!this.gtmService?.event) {
      return;
    }

    this.gtmEvent = cloneDeep(this.gtmService.event[this.gtm]);
    if (this.isDirectiveMisued()) {
      return;
    }

    this.id = this.gtmId !== undefined ? `${this.gtmEvent.id}-${this.gtmId}` : this.gtmEvent.id;

    if (this.gtmAction) {
      this.gtmEvent.action += this.gtmAction;
    }

    if (this.gtmLabel) {
      this.gtmEvent.label = this.gtmService.label?.[this.gtmLabel] || this.gtmLabel;
    }

    if (this.gtmCategory) {
      this.gtmEvent.category = this.gtmService.category?.[this.gtmCategory] || this.gtmCategory;
    }

    if (this.gtmOn.length > 1) {
      this.gtmOn.forEach((eventName) => {
        this.renderer.listen(this.elRef.nativeElement, eventName, (event: Event) =>
          this.eventTrack(event, eventName)
        );
      });
    } else {
      this.renderer.listen(this.elRef.nativeElement, this.gtmOn[0], (event: Event) =>
        this.eventTrack(event)
      );
    }
  }

  eventTrack(event: Event, eventType?: string): void {
    let action = `${this.gtmEvent.action}`;

    if (!isEmpty(eventType)) {
      action = `[${eventType.toUpperCase()}] ${action}`;
    }
    const properties: any = {
      ...this.gtmProperties,
      eventType: event.type,
    };

    if (this.gtmEvent.category) {
      properties.category = this.gtmEvent.category;
    }
    if (this.gtmEvent.label) {
      properties.label = this.gtmEvent.label;
    }

    const gtmEventModel: GTMEventModel = {
      event: 'GenericEvent',
      eventCategory: properties.category,
      eventLabel: properties.label,
      eventAction: action,
      eventId: this.id,
      eventType,
      identityType: this.gtmService.identityType,
      partnerId: this.gtmService.partnerId,
      roleGroup: this.gtmService.userIotRole,
      selectedDeviceId: this.gtmService.selectedDeviceId,
      selectedGatewayId: this.gtmService.selectedGatewayId,
      userType: this.gtmService.analyticsClientGroup,
      viguideVersion,
    };

    window.dataLayer.push(gtmEventModel);
  }

  private isDirectiveMisued(): boolean {
    if (!this.gtmEvent) {
      if (environment.env !== 'production') {
        if (this.gtm) {
          console.warn(`This gtm tag is missing: ${this.gtm}`);
        } else {
          console.warn(`This element doen't have gtm attribute configured: ${this?.elRef?.nativeElement?.localName}
            What we know:
            gtmAction: ${this?.gtmAction}
            gtmCategory ${this?.gtmCategory}
            gtmLabel ${this?.gtmLabel}
          `);
        }
      }

      return true;
    }

    return false;
  }
}
